import type { ChatMessage, ReportProgressData, TextBlock } from "@/lib/chat-types";
import { createId, readString, toRecord } from "@/lib/fund-compliance/parse-primitives";

export { createId as createMessageId };

export function createProgressMessage(id: string, data: ReportProgressData): ChatMessage {
  return {
    id,
    role: "assistant",
    blocks: [{ type: "report-progress", reportProgress: data }],
    timestamp: Date.now(),
  };
}

export function createAssistantTextMessage(id: string, text: string, showStatus = false): ChatMessage {
  return {
    id,
    role: "assistant",
    blocks: [{ type: "text", content: text } satisfies TextBlock],
    showStatus,
    timestamp: Date.now(),
  };
}

/**
 * Extract text content from a WebSocket event payload.
 * Tries common field names: content, text, message, delta, parts.
 */
export function extractEventText(payload: unknown): string {
  const record = toRecord(payload);
  if (!record) return typeof payload === "string" ? payload : "";
  return toText(record.content ?? record.text ?? record.message ?? record.delta ?? record.parts);
}

/**
 * Recursively extract text from various payload shapes.
 */
export function toText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map((item) => toText(item)).filter(Boolean).join("\n\n");
  const record = toRecord(value);
  if (!record) return "";

  const directText =
    readString(record.content) ??
    readString(record.text) ??
    readString(record.message) ??
    readString(record.markdown) ??
    readString(record.delta);

  if (directText) return directText;

  if (Array.isArray(record.parts)) {
    return record.parts.map((part) => toText(part)).filter(Boolean).join("\n\n");
  }

  if (Array.isArray(record.content)) {
    return record.content.map((part) => toText(part)).filter(Boolean).join("\n\n");
  }

  return "";
}
